import { useI18n } from "@core/i18n/i18n";
import styled from "@emotion/styled";
import { ErrorMessage } from "@ui/components/common/error-message";
import { TextArea } from "@ui/components/inputs/text-input";
import { row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { InputTextareaProps } from "primereact/inputtextarea";
import React from "react";

/**
 * note about charlimit: it is not the same as maxlength property.
 * maxLength will prevent text to have length greater than this value, and truncate text if necessary.
 * charLimit will allow the textarea to have more chars than the limit, but put the textarea in error state.
 */
interface TextAreaWithCharLimitProps extends Omit<InputTextareaProps, "value" | "onChange"> {
	text: string;
	onTextChange: (text: string) => void;
	errorMessage?: string;
	charLimit: number;
	className?: string;
}

export const TextAreaWithCharLimit = React.forwardRef<HTMLTextAreaElement, TextAreaWithCharLimitProps>(
	({ errorMessage, charLimit, text, onTextChange, className, ...inputProps }, ref) => {
		const { format } = useI18n();
		const characterLeft = charLimit - text.length;
		return (
			<Container className={className}>
				<StyledTextArea
					ref={ref}
					{...inputProps}
					onKeyPress={e => {
						if (e.key === "Enter") {
							e.preventDefault();
						}
					}}
					value={text}
					onChange={e => onTextChange(e.target.value)}
					autoResize
					rows={1}
					error={!!errorMessage || characterLeft < 0}
				/>
				<LimitWrapper>
					<span className="p-size-xs-semi-bold">{format("global.textArea.limitCharacters", { limit: charLimit })}</span>
					<CharactersLeft className="p-size-xs-semi-bold" error={characterLeft < 0}>
						{characterLeft}
					</CharactersLeft>
				</LimitWrapper>
				<ErrorMessage>{errorMessage}</ErrorMessage>
			</Container>
		);
	},
);
TextAreaWithCharLimit.displayName = "TextInput";

const Container = styled.div`
	${stack(0, "spacebetween", "stretch")};
`;

const StyledTextArea = styled(TextArea)`
	margin-bottom: 4px !important;
`;

const LimitWrapper = styled.div`
	padding: 0px 8px;
	${row(16, "spacebetween", "center")};
`;

const CharactersLeft = styled.span<{ error: boolean }>`
	color: ${props => (props.error ? colors.rejection : colors.black)};
`;
